import { type FC } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { axisOptions, defaultNameSource, holderSlots, resolveTypeId } from '../domain/accountTypeCatalog'
import { clientStatusOf, custodianIdOf } from '../domain/contactFallback'
import { type ContactOption, type WizardAccount, type WizardHolder } from '../domain/types'

interface AccountTypeFieldsProps {
  accountTypes: AccountType[]
  selectors: AccountOpeningTypeSelector[]
  account: WizardAccount
  onChange: (account: WizardAccount) => void
  connector: string
  contactOptions: ContactOption[]
  defaultHolderName?: string | null
  defaultHolderContactId?: string | null
}

export const AccountTypeFields: FC<AccountTypeFieldsProps> = ({
  accountTypes,
  selectors,
  account,
  onChange,
  connector,
  contactOptions,
  defaultHolderName,
  defaultHolderContactId,
}) => {
  const axisWidth = selectors.length > 2 ? 4 : 6

  const axisEnabled = (axisIndex: number) => selectors.slice(0, axisIndex).every((selector) => !!account.selection[selector.field])

  const handleAxisChange = (axisIndex: number, value: string) => {
    const selection = { ...account.selection, [selectors[axisIndex].field]: value }
    selectors.slice(axisIndex + 1).forEach((selector) => delete selection[selector.field])

    const typeId = resolveTypeId(accountTypes, selectors, selection)
    const slots = holderSlots(accountTypes, typeId)
    const launchSlot = slots.findIndex((slot) => slot.kind === 'individual')
    const launchContact = contactOptions.find((option) => option.id === defaultHolderContactId)
    const launchExternalId = custodianIdOf(launchContact, connector)
    const holders: WizardHolder[] = slots.map((slot, index) => {
      if (index === launchSlot && defaultHolderContactId) {
        return {
          roleCode: slot.roleCode,
          nameSource: 'contact',
          name: defaultHolderName ?? '',
          contactId: defaultHolderContactId,
          clientStatus: clientStatusOf(launchContact, connector),
          ...(launchExternalId ? { externalId: launchExternalId } : {}),
        }
      }
      return {
        roleCode: slot.roleCode,
        nameSource: slot.kind === 'organization' ? 'manual' : defaultNameSource(contactOptions),
        name: '',
        contactId: null,
        clientStatus: 'new',
      }
    })
    onChange({ ...account, selection, typeId, holders, schemaSnapshot: {}, schemaFetchedAt: undefined, values: {}, citations: {} })
  }

  return (
    <Row className='g-3'>
      {selectors.map((selector, axisIndex) => {
        const options: SelectOption[] = axisOptions(accountTypes, selectors, account.selection, axisIndex).map((option) => ({
          id: option.value,
          label: option.label,
        }))
        const chosen = account.selection[selector.field] ?? ''
        const enabled = axisEnabled(axisIndex)
        return (
          <Col key={selector.field} md={axisWidth} className='d-flex flex-column zocks-gap-0.5'>
            <Form.Label htmlFor={`${account.accountRefId}-${selector.field}`} className='paragraph-small mb-0'>
              {selector.label}
            </Form.Label>
            <SelectDropdown
              toggleId={`${account.accountRefId}-${selector.field}`}
              key={options.map((option) => option.id).join('|')}
              ariaLabel={selector.label}
              options={options}
              initialSelectedOption={options.find((option) => option.id === chosen) ?? null}
              placeholder={selector.placeholder ?? `Select a ${selector.label.toLowerCase()}…`}
              disabled={!enabled || options.length === 0}
              onOptionSelected={(option) => handleAxisChange(axisIndex, option.id)}
            />
          </Col>
        )
      })}
    </Row>
  )
}
